/**
 * Parses an OTLP-style header string into a headers object shared by every OTLP
 * exporter (logs, metrics, traces).
 *
 * Format follows the OpenTelemetry `OTEL_EXPORTER_OTLP_HEADERS` convention: a
 * comma-separated list of `key=value` pairs, e.g. `x-honeycomb-team=abc123`.
 * Whitespace around each key and value is trimmed. The value is taken verbatim
 * after the FIRST `=` (so a value may itself contain `=`, as base64 keys can).
 * Malformed entries — no `=`, or an empty key — are skipped rather than throwing:
 * a bad telemetry header must never break app startup.
 *
 * An empty / blank input yields `{}`, i.e. the browser posts to the trace
 * endpoint with NO auth header. That is the intended steady state once traces
 * egress via the OTel Collector, which injects the Honeycomb key server-side.
 * The header is populated only for the interim direct-to-Honeycomb path, where
 * the key is inlined into the public bundle — treat it as public and rotate it
 * on collector cutover. Lives here as a pure function so it is unit-testable
 * without booting the OTel SDK (same reasoning as `tracingGate` / `otelIngest`).
 */
export function parseOtlpHeaders(raw: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (!raw) {
    return headers;
  }
  for (const pair of raw.split(',')) {
    const eq = pair.indexOf('=');
    if (eq === -1) {
      continue;
    }
    const key = pair.slice(0, eq).trim();
    const value = pair.slice(eq + 1).trim();
    if (key) {
      headers[key] = value;
    }
  }
  return headers;
}
